import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { BuyerType, CartPublic, ShippingAddress } from "@dugads/shared";
import { useAuth } from "@/lib/auth-context";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { Minus, Plus, Trash2 } from "lucide-react";

function fmtMoney(cents: number, currency: string): string {
  return `${currency === "ZAR" ? "R " : `${currency} `}${(cents / 100).toFixed(2)}`;
}

const ZA_PROVINCES = [
  "Gauteng",
  "Western Cape",
  "KwaZulu-Natal",
  "Eastern Cape",
  "Free State",
  "Limpopo",
  "Mpumalanga",
  "North West",
  "Northern Cape",
];

export default function ShopCartPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cart, setCart] = useState<CartPublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [addr, setAddr] = useState<ShippingAddress>({
    line1: "", line2: "", town: "", province: "Gauteng", postal_code: "",
  });
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");

  // 094 -- buyer-type toggle. Only shown when the user has a school-admin role.
  // schoolOptions come from the user's own role scopes (user.roles is populated
  // by /auth/sync via MeResponse). Names get resolved lazily below so the
  // dropdown reads sensibly rather than showing raw scope_ids.
  const schoolRoleIds = useMemo(() => {
    if (!user) return [] as string[];
    return [...new Set(user.roles.filter((r) => r.role_type === "school_admin").map((r) => r.scope_id))];
  }, [user]);
  const [schoolOptions, setSchoolOptions] = useState<Array<{ id: string; name: string }>>([]);
  useEffect(() => {
    if (schoolRoleIds.length === 0) { setSchoolOptions([]); return; }
    // Resolve names once per mount; failure just falls back to scope_id.
    Promise.all(
      schoolRoleIds.map(async (id) => {
        try {
          const r = await api.getSchool(id);
          return { id, name: r.school.name };
        } catch {
          return { id, name: id };
        }
      }),
    ).then((opts) => setSchoolOptions(opts));
  }, [schoolRoleIds]);
  const canActAsSchool = schoolRoleIds.length > 0;
  const [buyerType, setBuyerType] = useState<BuyerType>("parent");
  const [buyerSchoolId, setBuyerSchoolId] = useState<string>(() => schoolOptions[0]?.id ?? "");

  // Effective-price preview for the toggled buyer type -- display only; the
  // server does authoritative resolution at submit.
  const previewTotals = useMemo(() => {
    if (!cart) return null;
    let subtotal = 0;
    let total = 0;
    for (const it of cart.items) {
      const pct = buyerType === "school" ? it.product.discount_pct_school : it.product.discount_pct_parent;
      const list = it.product.price_cents * it.quantity;
      const unit = pct && pct > 0 ? Math.round(it.product.price_cents * (1 - pct / 100)) : it.product.price_cents;
      subtotal += list;
      total += unit * it.quantity;
    }
    return { subtotal_cents: subtotal, total_cents: total, discount_cents: subtotal - total };
  }, [cart, buyerType]);

  const refresh = useCallback(async () => {
    if (!slug) return;
    // Need provider_id -- resolve via the provider endpoint
    const p = await api.getShopProvider(slug);
    const c = await api.getShopCart(p.provider.id);
    setCart(c.cart);
  }, [slug]);

  useEffect(() => {
    refresh()
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load cart"))
      .finally(() => setLoading(false));
  }, [refresh]);

  async function setQuantity(productId: string, quantity: number) {
    if (!cart) return;
    try {
      const r = await api.upsertCartItem({
        provider_id: cart.provider_id,
        product_id: productId,
        quantity: Math.max(0, quantity),
      });
      setCart(r.cart);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to update cart");
    }
  }

  async function removeLine(itemId: string) {
    try {
      const r = await api.removeCartItem(itemId);
      setCart(r.cart);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to remove item");
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!cart) return;
    setError(null);
    setSubmitting(true);
    try {
      const r = await api.submitShopOrder({
        provider_id: cart.provider_id,
        shipping_address: addr,
        contact_phone: phone || null,
        notes: notes || null,
        buyer_type: canActAsSchool ? buyerType : undefined,
        school_id: buyerType === "school" ? buyerSchoolId : null,
      });
      navigate(`/me/orders/${r.order.slug}?submitted=1`);
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to submit order");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <p className="text-muted-foreground" data-page="shop-cart" data-state="loading">Loading…</p>;
  if (error || !cart) {
    return (
      <div data-page="shop-cart" data-state="error" className="flex flex-col gap-4">
        <Breadcrumbs items={[{ label: "Events", to: "/discover" }, { label: "Shop", to: "/shop" }, { label: "Cart" }]} />
        <p className="text-sm text-destructive" data-error>{error ?? "Cart not found"}</p>
      </div>
    );
  }

  const empty = cart.items.length === 0;

  return (
    <div data-page="shop-cart" data-state={submitting ? "submitting" : "ready"} className="flex flex-col gap-6">
      <Breadcrumbs
        items={[
          { label: "Events", to: "/discover" },
          { label: "Shop", to: "/shop" },
          { label: cart.provider.name, to: `/shop/${slug}` },
          { label: "Cart" },
        ]}
      />
      <div>
        <h1 className="text-2xl font-semibold">Your cart</h1>
      </div>

      {empty ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground">Your cart is empty. <Link to={`/shop/${slug}`} className="text-primary hover:underline">Browse products</Link>.</CardContent></Card>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
          <Card>
            <CardHeader><CardTitle className="text-base">Items</CardTitle></CardHeader>
            <CardContent>
              <table className="w-full text-sm" data-region="cart-items">
                <thead>
                  <tr className="border-b border-border text-xs uppercase text-muted-foreground">
                    <th className="py-2 text-left">Item</th>
                    <th className="py-2 text-center">Qty</th>
                    <th className="py-2 text-right">Line total</th>
                    <th className="py-2 text-right"><span className="sr-only">Remove</span></th>
                  </tr>
                </thead>
                <tbody>
                  {cart.items.map((it) => (
                    <tr key={it.id} className="border-b border-border/60" data-item="cart-line" data-item-id={it.id}>
                      <td className="py-3">
                        <p className="font-medium">{it.product.name}</p>
                        <p className="text-xs text-muted-foreground">{it.product.sku} · {fmtMoney(it.unit_price_cents, cart.currency)}</p>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="outline" className="h-7 w-7 p-0" onClick={() => setQuantity(it.product_id, it.quantity - 1)} aria-label="Decrease quantity">
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-8 text-center tabular-nums">{it.quantity}</span>
                          <Button variant="outline" className="h-7 w-7 p-0" onClick={() => setQuantity(it.product_id, it.quantity + 1)} aria-label="Increase quantity">
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                      <td className="py-3 text-right font-medium tabular-nums">{fmtMoney(it.line_total_cents, cart.currency)}</td>
                      <td className="py-3 text-right">
                        <Button variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive" onClick={() => removeLine(it.id)} data-action="remove-item" aria-label="Remove">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2} className="pt-3 text-right text-sm">Subtotal (list)</td>
                    <td className="pt-3 text-right text-sm tabular-nums">{fmtMoney(previewTotals?.subtotal_cents ?? cart.subtotal_cents, cart.currency)}</td>
                    <td></td>
                  </tr>
                  {previewTotals && previewTotals.discount_cents > 0 && (
                    <tr>
                      <td colSpan={2} className="pt-1 text-right text-sm text-primary" data-field="discount-label">
                        {buyerType === "school" ? "School discount" : "Parent discount"}
                      </td>
                      <td className="pt-1 text-right text-sm tabular-nums text-primary">−{fmtMoney(previewTotals.discount_cents, cart.currency)}</td>
                      <td></td>
                    </tr>
                  )}
                  <tr>
                    <td colSpan={2} className="pt-3 text-right text-sm font-semibold">Total</td>
                    <td className="pt-3 text-right text-base font-semibold tabular-nums">{fmtMoney(previewTotals?.total_cents ?? cart.subtotal_cents, cart.currency)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
              {error && <p className="mt-3 text-sm text-destructive" data-error>{error}</p>}
            </CardContent>
          </Card>

          {canActAsSchool && (
            <Card data-region="buyer-type">
              <CardHeader><CardTitle className="text-base">Ordering for</CardTitle></CardHeader>
              <CardContent className="flex flex-col gap-3">
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={buyerType === "parent" ? "default" : "outline"}
                    onClick={() => setBuyerType("parent")}
                    data-action="buyer-parent"
                  >
                    Me (parent)
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={buyerType === "school" ? "default" : "outline"}
                    onClick={() => setBuyerType("school")}
                    data-action="buyer-school"
                  >
                    A school
                  </Button>
                </div>
                {buyerType === "school" && (
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="buyer-school">School</Label>
                    <select
                      id="buyer-school"
                      data-field="school_id"
                      value={buyerSchoolId}
                      onChange={(e) => setBuyerSchoolId(e.target.value)}
                      className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                      aria-label="School"
                      required
                    >
                      {schoolOptions.map((o) => (
                        <option key={o.id} value={o.id}>{o.name}</option>
                      ))}
                    </select>
                    <p className="text-xs text-muted-foreground">School pricing (where configured) will be applied on submit.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle className="text-base">Delivery</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="flex flex-col gap-3" data-region="form" data-form="checkout">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="line1">Address line 1</Label>
                  <Input id="line1" data-field="line1" value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} required />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="line2">Address line 2 <span className="text-xs text-muted-foreground">(optional)</span></Label>
                  <Input id="line2" data-field="line2" value={addr.line2 ?? ""} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} />
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="town">Town</Label>
                    <Input id="town" data-field="town" value={addr.town} onChange={(e) => setAddr({ ...addr, town: e.target.value })} required />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="postal_code">Postal code</Label>
                    <Input id="postal_code" data-field="postal_code" value={addr.postal_code} onChange={(e) => setAddr({ ...addr, postal_code: e.target.value })} required />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="province">Province</Label>
                  <select
                    id="province"
                    data-field="province"
                    title="Province"
                    aria-label="Province"
                    value={addr.province}
                    onChange={(e) => setAddr({ ...addr, province: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    required
                  >
                    {ZA_PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="phone">Contact phone <span className="text-xs text-muted-foreground">(optional)</span></Label>
                  <Input id="phone" data-field="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+27 82 000 1234" />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="notes">Notes for the provider <span className="text-xs text-muted-foreground">(optional)</span></Label>
                  <textarea
                    id="notes"
                    data-field="notes"
                    title="Notes for the provider"
                    aria-label="Notes for the provider"
                    placeholder="e.g. leave with security at gate B"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="min-h-16 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>
                <Button type="submit" data-action="submit-order" disabled={submitting}>
                  {submitting ? "Submitting…" : `Submit order · ${fmtMoney(previewTotals?.total_cents ?? cart.subtotal_cents, cart.currency)}`}
                </Button>
                <Link to={`/shop/${slug}`} className={buttonVariants({ variant: "outline", size: "sm" })} data-action="keep-shopping">Keep shopping</Link>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
